import { useState, useRef } from "react";
import { Upload, X, FileText, CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    location: "",
    project_type: "",
    service_type: "",
    timeframe: "",
    notes: "",
  });
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || []);
    addFiles(newFiles);
  };

  const addFiles = (newFiles) => {
    const pdfFiles = newFiles.filter((file) =>
      file.name.toLowerCase().endsWith(".pdf")
    );
    if (pdfFiles.length !== newFiles.length) {
      toast.error("Only PDF files are allowed for general estimating enquiries");
    }
    setFiles((prev) => [...prev, ...pdfFiles]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });
      files.forEach((file) => {
        formDataToSend.append("files", file);
      });

      const response = await axios.post(`${API}/quote-request`, formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data) {
        setIsSuccess(true);
        toast.success("Quote request submitted successfully!");
        setFormData({
          name: "",
          company: "",
          email: "",
          phone: "",
          location: "",
          project_type: "",
          service_type: "",
          timeframe: "",
          notes: "",
        });
        setFiles([]);
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(
        error.response?.data?.detail || "Failed to submit. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <section
        id="contact"
        className="py-16 md:py-24 border-t-4 border-black bg-zinc-50"
        data-testid="contact-section"
      >
        <div className="max-w-2xl mx-auto px-6 md:px-12 text-center">
          <div className="bg-white border-4 border-black p-12">
            <CheckCircle size={64} className="mx-auto mb-6 text-black" />
            <h2 className="font-heading font-bold text-3xl uppercase mb-4">
              Request Received
            </h2>
            <p className="font-body text-zinc-600 mb-8">
              We've got your plans and details. We'll review everything and get
              back to you within 1 business day to confirm scope, turnaround,
              and fee (all prices exclude GST).
            </p>
            <button
              onClick={() => setIsSuccess(false)}
              className="bg-black text-white h-12 px-8 uppercase font-bold tracking-widest border-2 border-black btn-brutal"
              data-testid="submit-another-btn"
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="contact"
      className="py-16 md:py-24 border-t-4 border-black bg-zinc-50"
      data-testid="contact-section"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        {/* Section header */}
        <div className="mb-12 md:mb-16 text-center">
          <span className="font-mono text-sm uppercase tracking-widest text-zinc-500">
            General Estimating Enquiry
          </span>
          <h2
            className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl uppercase mt-4"
            data-testid="contact-title"
          >
            Send the plans.
            <br />
            We'll do the thinking.
          </h2>
          <p className="font-body text-zinc-600 mt-4 max-w-xl mx-auto">
            For BOQ, labour/programme takeoffs, or full estimates.
          </p>
          
          {/* Tender Review CTA */}
          <div className="mt-6 p-4 bg-white border-2 border-black inline-block">
            <p className="font-body text-sm text-zinc-700 mb-2">
              Need an independent tender review before submission?
            </p>
            <Link
              to="/tender-red-review#tender-form"
              className="font-mono text-sm uppercase tracking-wider text-black hover:underline font-bold"
            >
              Submit a Tender Red Review →
            </Link>
          </div>
        </div>

        {/* Form with explicit method and action */}
        <form
          method="POST"
          action={`${API}/quote-request`}
          onSubmit={handleSubmit}
          className="bg-white border-4 border-black p-6 md:p-10"
          data-testid="quote-form"
        >
          {/* File Upload Area */}
          <div className="mb-8">
            <label 
              htmlFor="quote-file-upload"
              className="font-heading font-bold text-sm uppercase tracking-wider mb-3 block"
            >
              Upload Plans (PDF)
            </label>
            <div
              className={`border-2 border-dashed border-black p-8 text-center cursor-pointer transition-colors ${
                dragOver ? "bg-zinc-100" : "bg-zinc-50 hover:bg-zinc-100"
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              data-testid="file-drop-zone"
            >
              <Upload size={32} className="mx-auto mb-3 text-zinc-500" />
              <p className="font-body text-zinc-600 mb-2">
                Drag & drop PDF files here, or click to browse
              </p>
              <p className="font-mono text-xs text-zinc-400">
                Only PDF files accepted
              </p>
              <input
                type="file"
                id="quote-file-upload"
                name="files"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".pdf,application/pdf"
                multiple
                className="hidden"
                data-testid="file-input"
              />
            </div>

            {/* File list */}
            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 border-2 border-black bg-zinc-50"
                    data-testid={`file-item-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <FileText size={20} />
                      <span className="font-mono text-sm truncate max-w-xs">
                        {file.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="p-1 hover:bg-zinc-200"
                      data-testid={`remove-file-${index}`}
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Form Fields */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Name */}
            <div>
              <label 
                htmlFor="quote-name"
                className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
              >
                Name *
              </label>
              <input
                type="text"
                id="quote-name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full h-12 px-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none"
                data-testid="input-name"
              />
            </div>

            {/* Company */}
            <div>
              <label 
                htmlFor="quote-company"
                className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
              >
                Company *
              </label>
              <input
                type="text"
                id="quote-company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                required
                className="w-full h-12 px-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none"
                data-testid="input-company"
              />
            </div>

            {/* Email */}
            <div>
              <label 
                htmlFor="quote-email"
                className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
              >
                Email *
              </label>
              <input
                type="email"
                id="quote-email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full h-12 px-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none"
                data-testid="input-email"
              />
            </div>

            {/* Phone */}
            <div>
              <label 
                htmlFor="quote-phone"
                className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
              >
                Phone *
              </label>
              <input
                type="tel"
                id="quote-phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="w-full h-12 px-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none"
                data-testid="input-phone"
              />
            </div>

            {/* Location */}
            <div>
              <label 
                htmlFor="quote-location"
                className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
              >
                Location (NZ / AU) *
              </label>
              <select
                id="quote-location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                required
                className="w-full h-12 px-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none appearance-none cursor-pointer"
                data-testid="select-location"
              >
                <option value="">Select location</option>
                <option value="NZ - North Island">NZ - North Island</option>
                <option value="NZ - South Island">NZ - South Island</option>
                <option value="AU - QLD">AU - Queensland</option>
                <option value="AU - NSW">AU - New South Wales</option>
                <option value="AU - VIC">AU - Victoria</option>
                <option value="AU - Other">AU - Other</option>
              </select>
            </div>

            {/* Project Type */}
            <div>
              <label 
                htmlFor="quote-project-type"
                className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
              >
                Project Type *
              </label>
              <select
                id="quote-project-type"
                name="project_type"
                value={formData.project_type}
                onChange={handleInputChange}
                required
                className="w-full h-12 px-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none appearance-none cursor-pointer"
                data-testid="select-project-type"
              >
                <option value="">Select type</option>
                <option value="Residential - New Build">Residential - New Build</option>
                <option value="Residential - Renovation">Residential - Renovation</option>
                <option value="Residential - Extension">Residential - Extension</option>
                <option value="Civil - Drainage">Civil - Drainage</option>
                <option value="Civil - Driveways">Civil - Driveways</option>
                <option value="Civil - Retaining">Civil - Retaining</option>
                <option value="Civil - Earthworks">Civil - Earthworks</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Service Type */}
            <div>
              <label 
                htmlFor="quote-service-type"
                className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
              >
                What Do You Need? *
              </label>
              <select
                id="quote-service-type"
                name="service_type"
                value={formData.service_type}
                onChange={handleInputChange}
                required
                className="w-full h-12 px-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none appearance-none cursor-pointer"
                data-testid="select-service-type"
              >
                <option value="">Select service</option>
                <option value="Materials Takeoff (BOQ)">Materials Takeoff (BOQ)</option>
                <option value="Labour / Programme Takeoff">Labour / Programme Takeoff</option>
                <option value="Full Estimate">Full Estimate</option>
                <option value="General Estimate Check">General Estimate Check</option>
              </select>
            </div>

            {/* Timeframe */}
            <div>
              <label 
                htmlFor="quote-timeframe"
                className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
              >
                Timeframe *
              </label>
              <select
                id="quote-timeframe"
                name="timeframe"
                value={formData.timeframe}
                onChange={handleInputChange}
                required
                className="w-full h-12 px-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none appearance-none cursor-pointer"
                data-testid="select-timeframe"
              >
                <option value="">When do you need it?</option>
                <option value="ASAP / Rush">ASAP / Rush</option>
                <option value="Within 1 week">Within 1 week</option>
                <option value="Within 2 weeks">Within 2 weeks</option>
                <option value="No rush">No rush</option>
              </select>
            </div>
          </div>

          {/* Notes */}
          <div className="mt-6">
            <label 
              htmlFor="quote-notes"
              className="font-heading font-bold text-sm uppercase tracking-wider mb-2 block"
            >
              Project Notes / Additional Details
            </label>
            <textarea
              id="quote-notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={4}
              className="w-full p-4 border-2 border-black bg-white focus:shadow-brutal transition-shadow outline-none resize-none"
              placeholder="Any specific requirements, constraints, or details we should know about..."
              data-testid="textarea-notes"
            />
          </div>

          {/* Submit */}
          <div className="mt-8">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto bg-black text-white h-14 px-12 uppercase font-bold tracking-widest border-2 border-black btn-brutal flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Request
                  <Upload size={20} />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}
